import type { PaymentMethod, PaymentMethodOption } from "@pandas/http-client";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import RadioButton from "@/components/common/Buttons/RadioButton";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import logger from "@/lib/logging/logger";
import { cmsApi } from "@/services";
import { useStore } from "@/store";

export const Route = createFileRoute("/$locale/$partner/_app/_card/_paired/payment-methods")({
	component: PaymentMethodSelection,
});

function PaymentMethodSelection() {
	const { t, i18n } = useTranslation();
	const { goToNextStep } = useNavigationHelpers();

	const partnerCode = useStore((s) => s.instanceInfo?.partner.code);
	const configPaymentMethods = useStore((s) => s.config?.partnerSettings.paymentMethods);
	const setSelectedPaymentMethod = useStore((s) => s.setSelectedPaymentMethod);
	const setFinalizationInstructionsHtml = useStore((s) => s.setFinalizationInstructionsHtml);

	const [selectedCode, setSelectedCode] = useState<PaymentMethod | undefined>(undefined);
	const [options, setOptions] = useState<PaymentMethodOption[]>([]);

	useEffect(() => {
		setSelectedCode(undefined);

		if (!partnerCode) {
			setOptions([]);
			return;
		}

		const fetchData = async () => {
			try {
				const content = await cmsApi.getPaymentMethodsContent(partnerCode, i18n.language, configPaymentMethods);

				setOptions(content);
			} catch (error) {
				logger.warning("INFO", "Error fetching payment methods CMS content", error);
				setOptions([]);
			}
		};

		fetchData();
	}, [configPaymentMethods, i18n.language, partnerCode]);

	const pageTitle = options[0]?.title;

	const radioOptions = useMemo(
		() =>
			options.map((opt) => ({
				key: opt.code,
				content: (
					<div className="flex flex-col justify-center min-h-14">
						<h5 className="font-bold">{opt.buttonTitle}</h5>
						{opt.buttonDescription ? <h6>{opt.buttonDescription}</h6> : null}
					</div>
				),
			})),
		[options],
	);

	const onContinue = () => {
		if (!selectedCode) return;

		setSelectedPaymentMethod(selectedCode);

		const selectedOption = options.find((o) => o.code === selectedCode);
		setFinalizationInstructionsHtml(selectedOption?.finalizationInstructions ?? "");

		goToNextStep();
	};

	return (
		<div className="h-full w-full flex flex-col text-center items-center pt-10 sm:pt-20">
			<h3
				className="mb-4"
				dangerouslySetInnerHTML={{ __html: pageTitle ?? "" }}
				style={{ color: "inherit", wordWrap: "break-word" }}
			/>
			<div className="flex flex-col h-full justify-around overflow-y-auto pb-16 w-full max-w-3xl">
				<div className="min-h-72 lg:min-h-96 max-h-[40dvh] w-full relative">
					<img
						src={`/images/payment-method-selection.png`}
						alt="payment-method"
						className="w-full h-full object-contain"
					/>
				</div>
				<div className="flex flex-col gap-2 text-left py-6">
					<RadioButton
						value={selectedCode}
						themeMode="light"
						onChange={setSelectedCode}
						options={radioOptions}
					/>
				</div>
			</div>
			<div className="absolute bottom-0 left-0 bg-fixed-buttons w-full items-center flex flex-col-reverse sm:flex-row flex-wrap justify-center gap-2 lg:gap-4 mt-auto pb-6 sm:pb-10">
				<Button
					label={t("btn.Continue")}
					disabled={!selectedCode}
					onClick={onContinue}
				/>
			</div>
		</div>
	);
}
